import React, { useState, useEffect } from 'react';
import {
  isPlatformAuthenticatorAvailable,
  isWebAuthnSupported,
  registerPasskey,
  listPasskeys,
  deletePasskey,
  Passkey
} from '../../services/passkeyService';
import { changePassword, getSecurityActivity, LoginActivity } from '../../services/authService';
import PasswordInput from '../../components/common/PasswordInput';

const PasskeySettings: React.FC = () => {
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [loading, setLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [webAuthnSupported, setWebAuthnSupported] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Login activity
  const [recentLogins, setRecentLogins] = useState<LoginActivity[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const supported = isWebAuthnSupported();
    setWebAuthnSupported(supported);

    if (supported) {
      const available = await isPlatformAuthenticatorAvailable();
      setBiometricAvailable(available);
    }

    const keys = await listPasskeys();
    setPasskeys(keys);

    const activity = await getSecurityActivity();
    if (activity) {
      setRecentLogins(activity.recent_logins);
    }
  };

  const handleAddPasskey = async () => {
    setLoading(true);
    setMessage(null);

    const name = deviceName.trim() || getDefaultDeviceName();
    const result = await registerPasskey(name);

    if (result.success) {
      setMessage({ type: 'success', text: `${name} has been set up for biometric login!` });
      setDeviceName('');
      const keys = await listPasskeys();
      setPasskeys(keys);
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to set up biometric login' });
    }

    setLoading(false);
  };

  const handleDeletePasskey = async (id: number, name: string) => {
    if (!window.confirm(`Remove "${name}" from biometric login?`)) return;

    const success = await deletePasskey(id);
    if (success) {
      setMessage({ type: 'success', text: `Removed ${name}` });
      const keys = await listPasskeys();
      setPasskeys(keys);
    } else {
      setMessage({ type: 'error', text: 'Failed to remove passkey' });
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 12) {
      setPasswordMessage({ type: 'error', text: 'Password must be at least 12 characters' });
      return;
    }

    setPasswordLoading(true);
    const result = await changePassword(currentPassword, newPassword);

    if (result.success) {
      setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setPasswordMessage({ type: 'error', text: result.error || 'Failed to change password' });
    }

    setPasswordLoading(false);
  };

  const getDefaultDeviceName = () => {
    const ua = navigator.userAgent;
    if (/iPhone/i.test(ua)) return "My iPhone";
    if (/iPad/i.test(ua)) return "My iPad";
    if (/Mac/i.test(ua)) return "My Mac";
    if (/Windows/i.test(ua)) return "My Windows PC";
    if (/Android/i.test(ua)) return "My Android";
    return "My Device";
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.pageTitle}>Security Settings</h1>

      {/* Biometric Login Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <span style={styles.icon}>🔐</span>
          Biometric Login
        </h2>
        <p style={styles.description}>
          Use Face ID, Touch ID, or Windows Hello to sign in instantly without typing your password.
        </p>

        {message && (
          <div style={{
            ...styles.message,
            backgroundColor: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
            borderColor: message.type === 'success' ? '#86efac' : '#fecaca',
            color: message.type === 'success' ? '#166534' : '#dc2626',
          }}>
            {message.type === 'success' ? '✓' : '✗'} {message.text}
          </div>
        )}

        {!webAuthnSupported ? (
          <div style={styles.notAvailable}>
            <p>Biometric login is not supported in this browser.</p>
            <p>Try using Safari on iPhone/Mac, Chrome on Android, or Edge on Windows.</p>
          </div>
        ) : !biometricAvailable ? (
          <div style={styles.notAvailable}>
            <p>No biometric authenticator detected on this device.</p>
            <p>Make sure Face ID, Touch ID, or Windows Hello is set up on your device.</p>
          </div>
        ) : (
          <div style={styles.addPasskeyForm}>
            <input
              type="text"
              placeholder={`Device name (e.g., ${getDefaultDeviceName()})`}
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              style={styles.input}
            />
            <button
              onClick={handleAddPasskey}
              disabled={loading}
              style={styles.addButton}
            >
              {loading ? 'Setting up...' : '👆 Set Up Biometric Login'}
            </button>
          </div>
        )}

        {/* Registered Passkeys */}
        <div style={styles.passkeyList}>
          <h3 style={styles.subTitle}>Your Registered Devices</h3>
          {passkeys.length === 0 ? (
            <p style={styles.emptyText}>
              No devices registered yet. Add this device above to get started!
            </p>
          ) : (
            <ul style={styles.list}>
              {passkeys.map((pk) => (
                <li key={pk.id} style={styles.listItem}>
                  <div style={styles.passkeyInfo}>
                    <span style={styles.deviceName}>📱 {pk.device_name}</span>
                    <span style={styles.dates}>
                      Added {formatDate(pk.created_at)}
                      {pk.last_used_at && (
                        <> • Last used {formatDate(pk.last_used_at)}</>
                      )}
                    </span>
                  </div>
                  <button
                    style={styles.removeButton}
                    onClick={() => handleDeletePasskey(pk.id, pk.device_name)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Change Password Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <span style={styles.icon}>🔑</span>
          Change Password
        </h2>

        {passwordMessage && (
          <div style={{
            ...styles.message,
            backgroundColor: passwordMessage.type === 'success' ? '#f0fdf4' : '#fef2f2',
            borderColor: passwordMessage.type === 'success' ? '#86efac' : '#fecaca',
            color: passwordMessage.type === 'success' ? '#166534' : '#dc2626',
          }}>
            {passwordMessage.type === 'success' ? '✓' : '✗'} {passwordMessage.text}
          </div>
        )}

        <form onSubmit={handleChangePassword} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Current Password</label>
            <PasswordInput
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              style={styles.input}
              required
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>New Password</label>
            <PasswordInput
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={styles.input}
              required
              minLength={12}
            />
            <span style={styles.hint}>Must be at least 12 characters</span>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Confirm New Password</label>
            <PasswordInput
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={styles.input}
              required
            />
          </div>
          <button
            type="submit"
            disabled={passwordLoading}
            style={styles.submitButton}
          >
            {passwordLoading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Recent Login Activity */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <span style={styles.icon}>📋</span>
          Recent Login Activity
        </h2>

        {recentLogins.length === 0 ? (
          <p style={styles.emptyText}>No recent login activity</p>
        ) : (
          <ul style={styles.list}>
            {recentLogins.map((login, index) => (
              <li key={index} style={styles.activityItem}>
                <span style={{
                  ...styles.activityStatus,
                  color: login.successful ? '#22c55e' : '#ef4444'
                }}>
                  {login.successful ? '✓' : '✗'}
                </span>
                <span style={styles.activityDetails}>
                  {login.successful ? 'Successful login' : 'Failed login attempt'}
                  <span style={styles.activityMeta}>
                    {formatDate(login.attempted_at)} • {login.ip_address}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '2rem',
  },
  pageTitle: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: '#1f2937',
    marginBottom: '2rem',
  },
  section: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#374151',
    margin: '0 0 0.5rem 0',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  icon: {
    fontSize: '1.25rem',
  },
  description: {
    color: '#6b7280',
    marginBottom: '1.5rem',
  },
  message: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: '1px solid',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  notAvailable: {
    backgroundColor: '#fef3c7',
    border: '1px solid #fcd34d',
    borderRadius: '8px',
    padding: '1rem',
    color: '#92400e',
  },
  addPasskeyForm: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap' as const,
  },
  input: {
    flex: 1,
    minWidth: '200px',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    outline: 'none',
  },
  addButton: {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #2D5A27 0%, #4A7C42 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  passkeyList: {
    marginTop: '2rem',
  },
  subTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '1rem',
  },
  emptyText: {
    color: '#9ca3af',
    fontStyle: 'italic',
  },
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    marginBottom: '0.5rem',
  },
  passkeyInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  deviceName: {
    fontWeight: 600,
    color: '#1f2937',
  },
  dates: {
    fontSize: '0.85rem',
    color: '#6b7280',
  },
  removeButton: {
    padding: '0.5rem 1rem',
    fontSize: '0.9rem',
    color: '#dc2626',
    backgroundColor: 'transparent',
    border: '1px solid #dc2626',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
    maxWidth: '400px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  },
  label: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#374151',
  },
  hint: {
    fontSize: '0.8rem',
    color: '#9ca3af',
  },
  submitButton: {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: 600,
    backgroundColor: '#1f2937',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  activityItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    marginBottom: '0.5rem',
  },
  activityStatus: {
    fontSize: '1rem',
    fontWeight: 600,
  },
  activityDetails: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
    fontSize: '0.9rem',
    color: '#374151',
  },
  activityMeta: {
    fontSize: '0.8rem',
    color: '#9ca3af',
    display: 'block',
  },
};

export default PasskeySettings;
